import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { BridgeMessageFromType, BridgeNativeType } from '../../model/types/bridge-types';
import { ColorProvider } from '../color/color';
import { LoggerEvent, LoggerProvider } from '../logger/logger';

declare let window;

@Injectable()
export class NativeLiquidGlassBarProvider {

  private vueProvider: any;
  private lastConfig: any = null;
  private lastBarHeight = 0;
  private desiredVisible = false;
  private shown = false;
  private iframeHidden = true;
  private openModalCount = 0;
  private vueOverlaySuppressed = false;
  private tapListenerRegistered = false;
  private visibilityChain: Promise<void> = Promise.resolve();

  constructor(private platform: Platform) {}

  setVueProvider(vueProvider: any) {
    this.vueProvider = vueProvider;
  }

  async processNativeRequest(type: string, message: any): Promise<any> {
    const request = (message && message.request) || {};
    if (type === BridgeNativeType.LiquidGlassBar.IsAvailable) {
      return { available: await this.isBarAvailable() };
    }
    if (type === BridgeNativeType.LiquidGlassBar.Show) {
      return this.handleShowRequest(request);
    }
    if (type === BridgeNativeType.LiquidGlassBar.Update) {
      this.lastConfig = this.withBrandAccent(request);
      if (this.shown) {
        await this.execPlugin('update', [this.lastConfig]);
      }
      return { updated: true };
    }
    if (type === BridgeNativeType.LiquidGlassBar.Hide) {
      this.desiredVisible = false;
      await this.applyVisibility();
      return { hidden: true };
    }
    if (type === BridgeNativeType.LiquidGlassBar.SetSuppressed) {
      this.vueOverlaySuppressed = !!request.suppressed;
      await this.applyVisibility();
      return { suppressed: this.vueOverlaySuppressed };
    }
    throw new Error(`Unknown liquid glass bar function: ${type}`);
  }

  // The Vue bundle hard-codes accentColor '#FF6100' for every brand; the Send tint follows the
  // brand theme's --app-primary instead. Unresolved, the plugin falls back to its brand default.
  private withBrandAccent(request: any): any {
    const accentColor = ColorProvider.getColorValue('--app-primary') || undefined;
    return { ...request, accentColor };
  }

  private async handleShowRequest(request: any): Promise<any> {
    this.lastConfig = this.withBrandAccent(request);
    this.desiredVisible = true;
    this.reconcileModalCount();
    try {
      await this.applyVisibility();
    } catch (ex) {
      this.desiredVisible = false;
      throw ex;
    }
    if (this.shown)
      return { barHeight: this.lastBarHeight };
    let barHeight = this.lastBarHeight;
    try {
      const result = await this.execPlugin('getBarHeight', []);
      barHeight = (result && result.barHeight) || this.lastBarHeight;
    } catch (ex) {
      barHeight = this.lastBarHeight;
    }
    return { barHeight };
  }

  setIframeHidden(hidden: boolean) {
    if (this.iframeHidden === hidden)
      return;
    this.iframeHidden = hidden;
    if (!hidden)
      this.reconcileModalCount();
    this.applyVisibilitySafe();
  }

  notifyModalOpened() {
    this.openModalCount++;
    this.applyVisibilitySafe();
  }

  notifyModalDismissed() {
    if (this.openModalCount > 0)
      this.openModalCount--;
    this.reconcileModalCount();
    this.applyVisibilitySafe();
  }

  // Unpaired open/dismissed notifications leak; no overlay left in the DOM means the count is stale.
  private reconcileModalCount() {
    if (this.openModalCount > 0 &&
        !document.querySelector('ion-modal, ion-alert, ion-action-sheet, ion-popover'))
      this.openModalCount = 0;
  }

  reset() {
    this.desiredVisible = false;
    this.lastConfig = null;
    this.iframeHidden = true;
    this.vueOverlaySuppressed = false;
    this.setContainerFullBleed(false);
    this.applyVisibilitySafe();
  }

  private get plugin() {
    return window.cordova && window.cordova.plugins ? window.cordova.plugins.liquidGlassBar : null;
  }

  private availabilityCache: boolean | null = null;

  async isBarAvailable(): Promise<boolean> {
    if (this.availabilityCache === null)
      this.availabilityCache = await this.isAvailable();
    return this.availabilityCache;
  }

  private async isAvailable(): Promise<boolean> {
    if (!this.platform.is('cordova') || !this.platform.is('ios') || !this.plugin)
      return false;
    try {
      const available = await this.execPlugin('isAvailable', []);
      return available === true;
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.PluginError, 'liquidGlassBar isAvailable failed');
      return false;
    }
  }

  private applyVisibilitySafe() {
    this.applyVisibility().catch((ex) => {
      LoggerProvider.exception(this, ex, LoggerEvent.PluginError, 'liquidGlassBar visibility change failed');
    });
  }

  private async applyVisibility(): Promise<void> {
    const run = this.visibilityChain.then(() => this.runVisibility());
    this.visibilityChain = run.catch(() => undefined);
    return run;
  }

  private async runVisibility(): Promise<void> {
    const logicalOn = this.desiredVisible && !!this.lastConfig;
    // Bleed tracks the logical bar state; toggling it on transient suppression resizes the iframe.
    this.setContainerFullBleed(logicalOn);
    const shouldShow = logicalOn && !this.iframeHidden && this.openModalCount === 0 && !this.vueOverlaySuppressed;
    if (shouldShow === this.shown)
      return;
    if (shouldShow) {
      this.registerTapListener();
      try {
        const configAtShow = this.lastConfig;
        const result = await this.execPlugin('show', [configAtShow]);
        this.lastBarHeight = (result && result.barHeight) || this.lastBarHeight;
        this.shown = true;
        // An Update landing mid-show only stores its config — push it or the bar keeps the stale one.
        if (this.lastConfig !== configAtShow) {
          await this.execPlugin('update', [this.lastConfig]);
        }
      } catch (ex) {
        this.setContainerFullBleed(false);
        throw ex;
      }
    } else {
      this.shown = false;
      await this.execPlugin('hide', []);
    }
  }

  // The Vue-side clearance already covers the bottom safe area while the bar floats.
  private setContainerFullBleed(fullBleed: boolean) {
    const container = document.getElementById('vue-container');
    if (container)
      container.classList.toggle('glass-bar-fullbleed', fullBleed);
  }

  private registerTapListener() {
    if (this.tapListenerRegistered || !this.plugin)
      return;
    this.tapListenerRegistered = true;
    this.plugin.setTapListener((event) => {
      const tabId = event && event.tabId;
      if (tabId && this.vueProvider && this.vueProvider.isConnected) {
        this.vueProvider.postMessage(BridgeMessageFromType.LiquidGlassTabTapped, { tabId });
      }
    }, (error) => {
      LoggerProvider.error(this, LoggerEvent.PluginError, `liquidGlassBar tap listener error: ${error}`);
    });
  }

  private async execPlugin(method: string, args: any[]): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.plugin) {
        reject(new Error('liquidGlassBar plugin not available'));
        return;
      }
      this.plugin[method](...args, resolve, reject);
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/native/native-liquid-glass-bar.ts